<template>
    <v-card>
        <v-card-title>
            <div class="mr-3">{{ $t('event.document') }}</div>
            <v-spacer></v-spacer>
            <v-btn v-if="isEditOrderMode" color="red" dark class="ml-3" @click="cancelEditOrder">
                {{ $t('Cancel') }}
                <v-icon right>mdi-close-circle-outline</v-icon>
            </v-btn>
            <v-btn v-if="isEditOrderMode" color="green" dark class="ml-3" @click="saveEditOrder">
                {{ $t('Save') }}
                <v-icon right>mdi-check</v-icon>
            </v-btn>
            <v-btn v-if="!isEditOrderMode" color="primary" @click="initEditOrder">
                {{ $t('Reorder') }}
                <v-icon right>mdi-swap-vertical</v-icon>
            </v-btn>
            <div class="button-spacer"></div>
            <v-btn color="primary" @click="initAddDocument">
                {{ $t('add') }}
                <v-icon right>mdi-plus</v-icon>
            </v-btn>
        </v-card-title>
        <v-data-table :headers="headers" :items="tableDocument" :loading="isDocumentPending" @pagination="currentPagination">
            <template v-slot:item.scroller="{ value }">
                <div v-show="isEditOrderMode" class="handle"><v-btn icon><v-icon>drag_handle{{ value }}</v-icon></v-btn>
                </div>
                <div v-show="!isEditOrderMode"></div>
            </template>
            <template v-slot:item.order="{ value }">
                {{ value }}
            </template>
            <template v-slot:item.actions="{ item }">
                <v-btn color="primary" class="ma-2" :disabled="isEditOrderMode" @click="initEditDocument(item)">
                    {{ $t('edit') }}
                    <v-icon right>mdi-pencil</v-icon>
                </v-btn>
                <v-btn color="red" :dark="!isEditOrderMode" class="ma-2" :disabled="isEditOrderMode"
                    @click="deleteDocument(item)">
                    {{ $t('delete') }}
                    <v-icon right>mdi-delete</v-icon>
                </v-btn>
            </template>
        </v-data-table>

        <v-dialog v-model="editDocumentDialogShown" max-width="600px" persistent scrollable :retain-focus="false">
            <v-card>
                <v-card-title>
                    {{ $t('event.document') }}
                </v-card-title>
                <v-card-text>
                    <v-container>
                        <v-form v-model="isEditDocumentFormValid">
                            <v-row>
                                <v-col class="mb-0 pb-0">
                                    <v-text-field outlined :label="$t('event_document.key')"
                                        :placeholder="$t('event_document.key')" v-model="editingDocument.key"></v-text-field>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col class="mb-0 pb-0">
                                    <v-text-field outlined :label="$t('event_document.name')"
                                        :placeholder="$t('event_document.name')"
                                        v-model="editingDocument.name"></v-text-field>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col class="mb-0 pb-0">
                                    <v-text-field outlined :label="$t('event_document.category_1')"
                                        :placeholder="$t('event_document.category_1')"
                                        v-model="editingDocument.category_1"></v-text-field>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col class="mb-0 pb-0">
                                    <v-text-field outlined :label="$t('event_document.category_2')"
                                        :placeholder="$t('event_document.category_2')"
                                        v-model="editingDocument.category_2"></v-text-field>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col class="mb-0 pb-0">
                                    <v-text-field outlined :label="$t('event_document.link_1')"
                                        :placeholder="$t('event_document.link_1')"
                                        v-model="editingDocument.link_1"></v-text-field>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col class="mb-0 pb-0">
                                    <v-text-field outlined :label="$t('event_document.link_2')"
                                        :placeholder="$t('event_document.link_2')"
                                        v-model="editingDocument.link_2"></v-text-field>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col class="mb-0 pb-0">
                                    <v-text-field outlined :label="$t('event_document.link_3')"
                                        :placeholder="$t('event_document.link_3')"
                                        v-model="editingDocument.link_3"></v-text-field>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col class="mb-0 pb-0">
                                    <v-text-field outlined :label="$t('event_document.type')"
                                        :placeholder="$t('event_document.type')"
                                        v-model="editingDocument.type"></v-text-field>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col class="mb-0 pb-0">
                                    <v-text-field outlined :label="$t('event_document.icon')"
                                        :placeholder="$t('event_document.icon')"
                                        v-model="editingDocument.icon"></v-text-field>
                                </v-col>
                            </v-row>
                        </v-form>
                    </v-container>
                </v-card-text>
                <v-divider></v-divider>
                <v-card-actions>
                    <v-btn color="secondary" @click="confirmCancelItemDialogShown = true">{{ $t('cancel') }}</v-btn>
                    <v-spacer></v-spacer>
                    <v-btn color="primary" @click="confirmSaveItemDialogShown = true"
                        :disabled="!isEditDocumentFormValid">{{ $t('save') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <v-overlay :z-index="999" v-model="overlay">
            <v-progress-circular indeterminate size="64"></v-progress-circular>
        </v-overlay>
        <PopupAlert v-model="confirmCancelItemDialogShown" title="Warning"
            message="Confirm to cancel your amendments?" :onYes="cancelEditDocumentItem"
            :onNo="function () { confirmCancelItemDialogShown = false; }"></PopupAlert>
        <PopupAlert v-model="confirmSaveItemDialogShown" title="Confirm"
            message="Confirm to save your document?" :onYes="saveDocument"
            :onNo="function () { confirmSaveItemDialogShown = false; }"></PopupAlert>
    </v-card>
</template>

<style scoped>
    .button-spacer {
        width: 10px;
    }
</style>

<script>
import { computed, ref, onMounted } from "@vue/composition-api";
import { useFind } from "feathers-vuex";
import { DateTime } from "luxon";
import { cloneDeep } from "lodash";
import PopupAlert from "../../components/PopupAlert";
import Sortable from "sortablejs";

export default {
    name: 'EventDocument',
    components: { PopupAlert },
    setup(props, { root }) {
        const { EventDocument } = root.$FeathersVuex.api;

        const eventId = computed(() => root.$route.params.eventId);

        const documentParams = computed(() => {
            return {
                query: {
                    eventId: eventId.value
                }
            }
        });
        const { items: documentItems, isPending: isDocumentPending } = useFind({
            model: EventDocument,
            params: documentParams,
        });
        // Data Table
        const headers = ref([
            { text: '', value: 'scroller', width: 80 },
            { text: root.$t('event_document.order'), value: 'order' },
            { text: root.$t('event_document.key'), value: 'key' },
            { text: root.$t('event_document.name'), value: 'name' },
            { text: root.$t('event_document.category_1'), value: 'category_1' },
            { text: root.$t('event_document.category_2'), value: 'category_2' },
            // { text: root.$t('event_document.link_1'), value: 'link_1' },
            // { text: root.$t('event_document.link_2'), value: 'link_2' },
            // { text: root.$t('event_document.link_3'), value: 'link_3' },
            { text: root.$t('event_document.type'), value: 'type' },
            { text: root.$t('event_document.icon'), value: 'icon' },
            { text: '', value: 'actions' },
        ]);

        // Order
        const isEditOrderMode = ref(false);
        const didOrderChange = ref(false);
        const editOrderDocument = ref([]);
        const overlay = ref(false);

        // Add / Edit
        const editDocumentDialogShown = ref(false);
        const confirmCancelItemDialogShown = ref(false);
        const confirmSaveItemDialogShown = ref(false);
        const isEditDocumentFormValid = ref(false);
        const editingDocument = ref(new EventDocument({ eventId: eventId.value }));
        const initAddDocument = function () {
            editingDocument.value = new EventDocument({ eventId: eventId.value });
            editingDocument.value.order = documentItems.value.length + 1;
            editDocumentDialogShown.value = true;
        };
        const initEditDocument = function (document) {
            editingDocument.value = cloneDeep(document);
            editDocumentDialogShown.value = true;
        };
        const deleteDocument = function (document) {
            if (confirm(root.$t('event_document.confirm_delete'))) {
                document.remove();
            }
        };
        const saveDocument = async function () {
            editingDocument.value.save().then(() => {
                EventDocument.get(editingDocument.value.id);
                editDocumentDialogShown.value = false;
                confirmSaveItemDialogShown.value = false;
                root.$store.dispatch('showSnackBar', root.$t('event_document.save_successful'));
            }, root.$handleFeatherError.bind(root));
        };

        const previewHtmlContentOption = {
            modules: {
                toolbar: false,
            },
            placeholder: '',
            theme: 'bubble',
        };

        const cancelEditDocumentItem = function () {
            editDocumentDialogShown.value = false;
            confirmCancelItemDialogShown.value = false;
        }

        const initEditOrder = function () {
            didOrderChange.value = false
            isEditOrderMode.value = true
            editOrderDocument.value = cloneDeep(sortedDocument.value)
        }

        const saveEditOrder = async function () {
            if (!didOrderChange.value) {
                isEditOrderMode.value = false
                return;
            }

            overlay.value = true
            for (let i = 0; i < editOrderDocument.value.length; i++) {
                await editOrderDocument.value[i].save().then(() => {
                    EventDocument.get(editOrderDocument.value[i].id)
                })
            }
            isEditOrderMode.value = false
            overlay.value = false
        }

        const cancelEditOrder = function () {
            isEditOrderMode.value = false
        }
        const quickSortDocument = function (sortDocument) {
            if (sortDocument.length < 2) {
                return sortDocument
            }

            const chosenIndex = sortDocument.length - 1
            const chosen = sortDocument[chosenIndex]
            const a = []
            const b = []
            for (let i = 0; i < chosenIndex; i++) {
                const temp = sortDocument[i].order
                temp < chosen.order ? a.push(sortDocument[i]) : b.push(sortDocument[i])
            }
            const output = [...quickSortDocument(a), chosen, ...quickSortDocument(b)]
            return output
        }

        const sortedDocument = computed(() => {
            return quickSortDocument(documentItems.value)
        })
        const currentStart = ref(0);// Holds the index of starting item in speakerItemList in the current page of v-table
        const currentPagination = function (e) { // Update current page
            currentStart.value = e.pageStart
        }
        onMounted(() => {
            let table = document.querySelector('.v-data-table tbody');
            Sortable.create(table,
                {
                    handle: ".handle", // Look for class with name "handle"
                    onEnd({ newIndex, oldIndex }) {
                        didOrderChange.value = true;
                        newIndex += currentStart.value;
                        oldIndex += currentStart.value;
                        const rowSelected = editOrderDocument.value.splice(oldIndex, 1)[0];
                        editOrderDocument.value.splice(newIndex, 0, rowSelected);

                        for (let i = 0; i < editOrderDocument.value.length; i++) {
                            editOrderDocument.value[i].order = i + 1
                        }
                        editOrderDocument.value = quickSortDocument(editOrderDocument.value);
                    }
                }
            )
        });

        const tableDocument = computed(() => {
            return isEditOrderMode.value ? editOrderDocument.value : sortedDocument.value
        })

        return {
            documentItems,
            isDocumentPending,

            headers,
            tableDocument,

            editDocumentDialogShown,
            confirmCancelItemDialogShown,
            confirmSaveItemDialogShown,
            initAddDocument,
            initEditDocument,
            deleteDocument,
            isEditDocumentFormValid,
            editingDocument,
            saveDocument,
            cancelEditDocumentItem,

            DateTime,
            getFileUrl: root.$getFileURL,

            previewHtmlContentOption,
            currentPagination,

            initEditOrder,
            saveEditOrder,
            cancelEditOrder,

            isEditOrderMode,
            overlay,
        }
    }
}
</script>
